/* global gtag */
/**
 * Interact with the Report API.
 */
import axios from '../axios-config';
import fallbackData from '../../config/admin/fallbackData';
import { exportCSVFile } from '../../src/services/csvHandlers';

const Report = {
  userId: null,
  carrierId: null,
  agencyId: null,
  endpoints: {
    prefix: 'reports',
    permissionsPrefix: 'agent', // Default value
    reportTypePrefix: 'carrier', // Default value
    suffix: 'activity-report',
    userActivity: '/reports/user-activity',
    carrierActivity: '/reports/carrier-activity', // Needs ?metric = premium | policies
    agencyActivity: '/reports/agency-activity', // Needs ?metric = premium | policies
    activityTable: '/reports/activity-report', // Needs dateRange, dimensions
    dailyActivityReport: '/reports/daily-activity/download', // Needs format, dateRange
    dailyActivityReportSuffix: 'daily-activity-report/download',
    recentQuotesActivity: '/reports/recent-quotes',
    recentSalesActivity: '/reports/recent-sales',
    carrierSalesActivity: '/reports/carrier-sales-activity',
    salesByState: 'reports/sales-by-state',
    salesByAgent: 'reports/sales-by-agent',
    salesByCarrier: 'reports/sales-by-carrier',
    quotesByCarrier: 'reports/quotes-by-carrier',
    salesByProduct: 'reports/sales-by-product',
  },
  getFallbackData(reportType) {
    return fallbackData[reportType];
  },
  populateFilters(responseFilters) {
    if (!responseFilters || responseFilters[''] === '') { return []; } // TODO: Remove once API sends proper data

    const filters = [];

    Object.keys(responseFilters).forEach((key) => {
      filters.push({
        internal_value: key,
        display_value: responseFilters[key],
        visible: true, // TODO: Use data values
        display_order: 0, // TODO: Use data values
      });
    });

    return filters;
  },
  /**
   * The primary dimension column needs to be at the beginning of the row. This
   * finds the primary dimension column and reorders the row to put it at the beginning.
   *
   * @param {*} columns
   * @param {*} filters
   */
  reorderColumns(columns, filters) {
    const columnsOrdered = [];
    let columnPrimaryDimension = false;

    switch (filters.reportType) { // TODO: Use 'dimensionProperty' from Report view
      case 'CarrierActivitySelected':
        columnPrimaryDimension = 'CarrierName';
        break;

      case 'TeamActivitySelected':
        columnPrimaryDimension = 'TeamName';
        break;

      case 'AgentActivitySelected':
        columnPrimaryDimension = 'AgentFullName';
        break;

      case 'StateActivitySelected':
        columnPrimaryDimension = 'StateTitleAbbrev';
        break;

      case 'ProductActivitySelected':
        columnPrimaryDimension = 'ProductName';
        break;

      default:
        break;
    }

    Object.keys(columns).forEach((item) => {
      if (item === columnPrimaryDimension) {
        columnsOrdered.unshift({ // Put primary dimension column at the beginning of the row
          columnProperty: item,
          columnName: columns[item],
        });
      } else {
        columnsOrdered.push({
          columnProperty: item,
          columnName: columns[item],
        });
      }
    });

    return columnsOrdered;
  },
  reportTitle(route, agencyName = false) {
    const reportTitle = (route.meta ? route.meta.title : '');

    return (agencyName ? `${agencyName} - ${reportTitle}` : reportTitle);
  },
  requestUserActivity(userId) {
    this.userId = userId;

    return axios.post(
      this.endpoints.userActivity, {
        userId: this.userId,
      },
    ).then(() => true).catch((error) => {
      // TODO: Use response (data or http code) to evaluate error

      window.console.warn('An error occurred while requesting user activity:', error);
      gtag('event', 'exception', {
        description: 'Error requesting user activity',
        fatal: false, // set to true if the error is fatal
      });

      return this.getFallbackData('empty');
    });
  },
  requestCarrierActivity(carrierId) {
    this.carrierId = carrierId;

    return axios.post(
      this.endpoints.carrierActivity, {
        carrierId: this.carrierId,
      },
    ).then(() => true).catch((error) => {
      // TODO: Use response (data or http code) to evaluate error

      window.console.warn('An error occurred while requesting carrier activity:', error);
      gtag('event', 'exception', {
        description: 'Error requesting carrier activity',
        fatal: false, // set to true if the error is fatal
      });

      return this.getFallbackData('empty');
    });
  },
  requestAgencyActivity(agencyId) {
    this.agencyId = agencyId;

    return axios.post(
      this.endpoints.agencyActivity, {
        carrierId: this.carrierId,
      },
    ).then(() => true).catch((error) => {
      // TODO: Use response (data or http code) to evaluate error

      window.console.warn('An error occurred while requesting agency activity:', error);
      gtag('event', 'exception', {
        description: 'Error requesting agency activity',
        fatal: false, // set to true if the error is fatal
      });

      return this.getFallbackData('empty');
    });
  },
  createEndpointUrl(reportType) {
    const userPermissions = (sessionStorage.getItem('permissions') ? sessionStorage.getItem('permissions').split(',') : []);

    switch (reportType) {
      case 'CarrierActivity':
      case 'CarrierActivitySelected':
      case 'CarrierActivityTop':
        this.endpoints.reportTypePrefix = 'carrier';
        if (userPermissions.indexOf('ViewAllAgenciesCarrierActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('ViewAgencyCarrierActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('ViewAgentCarrierActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'StateActivity':
      case 'StateActivitySelected':
      case 'StateActivityTop':
        this.endpoints.reportTypePrefix = 'state';
        if (userPermissions.indexOf('ViewAllAgenciesStateActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('ViewAgencyStateActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('ViewAgentStateActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'AgentActivity':
      case 'AgentActivitySelected':
      case 'AgentActivityTop':
        this.endpoints.reportTypePrefix = 'agent';
        if (userPermissions.indexOf('ViewAllAgenciesAgentActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('ViewAgencyAgentActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('ViewAgentAgentActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'TeamActivity':
      case 'TeamActivitySelect':
      case 'TeamActivityTop':
        this.endpoints.reportTypePrefix = 'team';
        if (userPermissions.indexOf('ViewAllAgenciesTeamActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('ViewAgencyTeamActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('ViewAgentTeamActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'ProductActivity':
      case 'ProductActivitySelected':
      case 'ProductActivityTop':
        this.endpoints.reportTypePrefix = 'product';
        if (userPermissions.indexOf('ViewAllAgenciesProductActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('ViewAgencyProductActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('ViewAgentProductActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'DailyActivityReport':
        this.endpoints.reportTypePrefix = '';
        if (userPermissions.indexOf('DownloadAllAgenciesDailyActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'allagencies';
        } else if (userPermissions.indexOf('DownloadAgencyDailyActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agency';
        } else if (userPermissions.indexOf('DownloadAgentDailyActivityReport') !== -1) {
          this.endpoints.permissionsPrefix = 'agent';
        }
        break;

      case 'RecentQuotes':
      case 'RecentSales':
      case 'CarrierSalesActivity':
      case 'SalesByState':
      case 'SalesByAgent':
      case 'SalesByCarrier':
      case 'QuotesByCarrier':
      case 'SalesByProduct':
        break;

      default:
        window.console.warn('No valid report type found. Using default.');
        break;
    }

    let endpointUrl;

    if (reportType === 'DailyActivityReport') {
      endpointUrl = `/${this.endpoints.prefix}/${this.endpoints.permissionsPrefix}-${this.endpoints.dailyActivityReportSuffix}`;
    } else if (reportType === 'RecentQuotes') {
      endpointUrl = this.endpoints.recentQuotesActivity;
    } else if (reportType === 'RecentSales') {
      endpointUrl = this.endpoints.recentSalesActivity;
    } else if (reportType === 'CarrierSalesActivity') {
      endpointUrl = this.endpoints.carrierSalesActivity;
    } else if (reportType === 'SalesByState') {
      endpointUrl = this.endpoints.salesByState;
    } else if (reportType === 'SalesByAgent') {
      endpointUrl = this.endpoints.salesByAgent;
    } else if (reportType === 'SalesByCarrier') {
      endpointUrl = this.endpoints.salesByCarrier;
    } else if (reportType === 'QuotesByCarrier') {
      endpointUrl = this.endpoints.quotesByCarrier;
    } else if (reportType === 'SalesByProduct') {
      endpointUrl = this.endpoints.salesByProduct;
    } else {
      endpointUrl = `/${this.endpoints.prefix}/${this.endpoints.permissionsPrefix}-${this.endpoints.reportTypePrefix}-${this.endpoints.suffix}`;
    }

    return endpointUrl;
  },
  requestActivityTable(filters, target) {
    const endpointUrl = this.createEndpointUrl(filters.reportType);
    let dateRangeOfTarget = null; // TODO: Use config values

    switch (target) {
      case 'primary':
        dateRangeOfTarget = filters.dateRange;
        break;

      case 'secondary':
        dateRangeOfTarget = filters.dateRangeSecondary;
        break;

      default:
        window.console.warn('Missing data target. Using default date range.', dateRangeOfTarget);
        break;
    }

    return axios.post(
      endpointUrl, {
        target, // Not currently used by API, but helpful in labeling the request as either 'primary' or 'secondary'
        dateFrom: dateRangeOfTarget, // TODO: Use corresponding value once API is ready
        dateTo: dateRangeOfTarget, // TODO: Use corresponding value once API is ready
        productId: filters.productId,
        dateRange: dateRangeOfTarget,
        reportType: filters.reportType, // i.e. "CarrierActivityTop"
        dimensions: filters.dimensions,
        filters: filters.filters, // TODO: Use corresponding value once API is ready
        rowCount: filters.rowCount, // TODO: Use corresponding value once API is ready
      },
    ).then((response) => {
      /* eslint no-param-reassign: ["error", { "props": false }] */
      response.data.rows = response.data.DataRows; // TODO: Use API property names

      // TODO: Include date range per row in API response
      response.data.rows.forEach((row, i) => {
        response.data.rows[i].dateRange = dateRangeOfTarget;
      });

      response.data.columns = response.data.Columns; // TODO: Use API property names

      response.data.columns = this.reorderColumns(response.data.columns, filters);

      response.data.hasRows = !!(response.data.rows.length); // Check if there are no rows of data

      return response.data;
    }).catch((error) => {
      // TODO: Use response (data or http code) to evaluate error

      window.console.warn('An error occurred while requesting carrier activity table:', error);
      gtag('event', 'exception', {
        description: 'Error requesting carrier activity table',
        fatal: false, // set to true if the error is fatal
      });

      return this.getFallbackData('empty');
    });
  },
  requestSummary(filters) {
    const endpointUrl = this.createEndpointUrl(filters.reportType);

    return axios.post(
      endpointUrl, {
        dateFrom: filters.dateRange, // TODO: Use corresponding value once API is ready
        dateTo: filters.dateRange, // TODO: Use corresponding value once API is ready
        dateRange: filters.dateRange,
        reportType: filters.reportType, // i.e. CarrierActivityTop
        dimensions: filters.dimensions,
        productId: filters.productId,
        filters: filters.filters, // TODO: Use corresponding value once API is ready
      },
    ).then((response) => {
      /* eslint no-param-reassign: ["error", { "props": false }] */
      response.data.rows = response.data.DataRows; // TODO: Use API property names
      response.data.columns = response.data.Columns; // TODO: Use API property names
      response.data.summary = response.data.DataRow; // TODO: Use API property names

      return response.data; // This response data is a an object for the Summary Block to use
    }).catch((error) => {
      // TODO: Use response (data or http code) to evaluate error

      window.console.warn('An error occurred while requesting report summary:', error);
      gtag('event', 'exception', {
        description: 'Error requesting report summary',
        fatal: false, // set to true if the error is fatal
      });

      return this.getFallbackData('empty');
    });
  },
  /**
   * @param {Object} dataFilters.format csv
   * @param {Object} dataFilters.dateRange Today || Yesterday || CurrentWeek || PreviousWeek || CurrentMonth || PreviousMonth
   */
  /**
   * @param {Object} dataFilters.format csv
   * @param {Object} dataFilters.dateRange Today || Yesterday || CurrentWeek || PreviousWeek || CurrentMonth || PreviousMonth
   */
  requestDailyActivityReport(dataFilters) {
    const endpointUrl = this.createEndpointUrl('DailyActivityReport'); // TODO: Use config values

    const CancelToken = axios.CancelToken;

    return axios.get(`${endpointUrl}?format=${dataFilters.format}&dateFrom=${dataFilters.dateFrom}&dateTo=${dataFilters.dateTo}&productTypeIds=${dataFilters.productType}`, {
      cancelToken: new CancelToken((c) => {
        // An executor function receives a cancel function as a parameter
        window.cancel = c;
      }),
    })
      .then((response) => {
        const dailyActivityReport = response.data;

        exportCSVFile(
          dailyActivityReport,
          'daily-activity-report',
        );

        return response;
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          window.console.warn('A cancel occurred while requesting daily activity report download:', error);
          gtag('event', 'exception', {
            description: 'Cancelled requesting daily activity report download',
            fatal: false, // set to true if the error is fatal
          });

          return false;
        }

        window.console.warn('An error occurred while requesting daily activity report download:', error);
        gtag('event', 'exception', {
          description: 'Error requesting daily activity report download',
          fatal: false, // set to true if the error is fatal
        });

        return Promise.reject(error);
      });
  },
};
/**
 * export service to used by different components
 */
export default Report;



// WEBPACK FOOTER //
// ./src/services/Report.js